import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { FestivalType, ShopbyFestivalComponentBenefit, ShopbyFestivalComponents } from '../../@types/aboutfishing'
import { ProfileRootType } from '../../@types/auth'
import { DownloadCoupon, IssuableAllCouponType } from '../../@types/coupon'
import { getIssuableAllCoupon, postCouponNoDownload } from '../../api/coupons'
import { postFestivalTypeAcc } from '../../api/login'
import { handlePostMessage } from '../../js/postMessage'
import { getParsedSessionStorageItem } from '../../js/script'

import { useMyContext } from '../provider/MyProvider'
import { useCommonContext } from '../provider/CommonProvider'
import { BtnHomeModuleMore } from '../Button'
import FestivalCoupon from './FestivalCoupon'

interface FestivalCouponModuleProps {
  el: ShopbyFestivalComponents
  userData: ProfileRootType | null | undefined
  memberCheckPoint: boolean
  festivalData: FestivalType | null
  getScrollMarginTop: string
}

const FestivalCouponModule = (
  { el, userData, memberCheckPoint, festivalData, getScrollMarginTop }: FestivalCouponModuleProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  // FIXME: 상태 및 로직 정리 부탁드립니다.
  const [checkDownload, setCheckDownload] = useState<DownloadCoupon[]>([]) // 각 쿠폰 초기 값 설정
  const [downloadCheck, setDownLoadCheck] = useState<DownloadCoupon[]>([]) // 각 쿠폰들 초기 값에서 해당 id번호에 해당되는 쿠폰이 다운로드 가능한지 확인
  const [couponData, setCouponData] = useState<IssuableAllCouponType[]>([]) // 쿠폰번호로 데이터 조회
  const [couponStateChange, setCouponStateChange] = useState<boolean>(false) // 쿠폰 다운로드 받았을때 쿠폰 정보 확인
  const [couponUnique, setCouponUnique] = useState<number[]>([]) //쿠폰 번호가 중복인지 체크해서 중복인 번호는 제거
  const [selectCouponArray, setSelectCouponArray] = useState<number[]>([]) // 쿠폰 사용안한배열
  const [allDownloadableCoupons, setAllDownloadableCoupons] = useState<IssuableAllCouponType[]>([]) // 다운로드 가능한 쿠폰 배열
  const [checkCount, setCheckCount] = useState<number>(0) // 다운로드 가능한 쿠폰 카운트
  const [isDownloadAllCoupon, setIsDownloadAllCoupon] = useState<boolean>(false) // 모든 혜택받기 버튼눌렀을때 받을수 있는지
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { setIsLogin, hasAccessToken } = useCommonContext()

  const postFestivalTypeAccHandler = useCallback(
    async (userData: ProfileRootType, festivalData: FestivalType, accTypes: ShopbyFestivalComponentBenefit[]) => {
      await postFestivalTypeAcc(Number(userData?.memberNo), festivalData.festival.id, accTypes[0]?.accumulationType)
    },
    []
  )

  const postCouponsHandler = useCallback(async (couponUnique: number[]) => {
    await Promise.all(
      couponUnique.map(async (element: number) => {
        try {
          await postCouponNoDownload(element)
        } catch (err) {
          console.error('다운로드 가능한 쿠폰이 없습니다.')
          // 필요시 사용자에게 알림을 표시하거나 상태를 업데이트할 수 있습니다.
          // 예: setError(`쿠폰 다운로드에 실패했습니다: ${element}`);
        }
      })
    )
  }, [])

  const loginInducementHandler = useCallback(() => {
    const app = getParsedSessionStorageItem('app')
    if (app) {
      handlePostMessage({ actionType: 'onSignInInducement' })
    } else {
      setIsLogin(true)
    }
  }, [setIsLogin])

  const showToast = useCallback(
    (message: string, btnMessage?: string, eventHandler?: () => void) => {
      setIsToast(true)
      setToastMessage(message)
      if (btnMessage) setToastBtnMessage(btnMessage)
      if (eventHandler) setToastEvent(() => eventHandler)
    },
    [setIsToast, setToastMessage, setToastBtnMessage, setToastEvent]
  )

  const resetCoupons = useCallback(() => {
    setAllDownloadableCoupons([])
    setCouponUnique([])
  }, [allDownloadableCoupons, couponUnique, couponStateChange])

  const toggleAllDownloadStates = useCallback(() => {
    setIsDownloadAllCoupon((prev) => !prev)
  }, [setIsDownloadAllCoupon])

  const homeModuleMoreButtonHandler = useCallback(
    async (el: ShopbyFestivalComponents) => {
      if (!hasAccessToken || !festivalData) {
        showToast('로그인이 필요한 서비스입니다.', '로그인', loginInducementHandler)
        return
      }

      if (Number(checkCount) === Number(downloadCheck.length)) {
        showToast('이미 적립금/쿠폰을 모두 받으셨습니다.')
        return
      }

      const accTypes = el.shopbyFestivalComponentBenefits.filter((el) => el.type === 'ad-hoc')
      try {
        if (userData && festivalData) {
          await postFestivalTypeAccHandler(userData, festivalData, accTypes)
        }
        if (couponUnique.length !== 0) {
          await postCouponsHandler(couponUnique)
          resetCoupons()
        }
        toggleAllDownloadStates()
        setCouponStateChange((prev) => !prev)
        showToast('적립금/쿠폰을 모두 받으셨습니다.')
      } catch (err) {
        console.log(err)
      }
    },
    [
      hasAccessToken,
      festivalData,
      checkCount,
      downloadCheck.length,
      userData,
      postFestivalTypeAccHandler,
      postCouponsHandler,
      resetCoupons,
      toggleAllDownloadStates,
      showToast,
      loginInducementHandler,
    ]
  )

  const getHomeModuleMoreButtonText = useMemo(
    () => (hasAccessToken && Number(checkCount) === Number(downloadCheck.length) ? '혜택 받기 완료' : '모든 혜택 받기'),
    [hasAccessToken, checkCount, downloadCheck.length]
  )

  //쿠폰정보 들고오기
  useEffect(() => {
    const fetchCoupons = async () => {
      if (hasAccessToken) {
        try {
          const res = await getIssuableAllCoupon()
          setCouponData(res.data)
        } catch (error) {
          console.error(error)
        }
      }
    }

    fetchCoupons()
  }, [couponStateChange, isDownloadAllCoupon, hasAccessToken])

  const removeDuplicates = (arr: number[]) => {
    return [...new Set(arr)]
  }

  useEffect(() => {
    if (selectCouponArray.length > 0) {
      const remove = removeDuplicates(selectCouponArray)
      setCouponUnique(remove)
    }
  }, [selectCouponArray])

  useEffect(() => {
    if (downloadCheck.length > 0) {
      const count = downloadCheck.filter((el: DownloadCoupon) => el.couponCheck === true).length
      setCheckCount(count)
    }
  }, [downloadCheck])

  return (
    <>
      <div
        className='benefit center section'
        id={`section${el.id}`}
        ref={ref}
        style={{ scrollMarginTop: getScrollMarginTop }}
      >
        <div className='head_img'>
          <img src={isMobile ? el.mobileImageUrl : el.pcImageUrl} alt='' />
        </div>
        <ul>
          {el.shopbyFestivalComponentBenefits.map((element, idx) => (
            <FestivalCoupon
              key={`coupon-${element.no}-${idx}`}
              element={element}
              idx={idx}
              userData={userData}
              allDownloadCoupons={isDownloadAllCoupon}
              allDownloadableCoupons={allDownloadableCoupons}
              setAllDownloadableCoupons={setAllDownloadableCoupons}
              setCouponStateChange={setCouponStateChange}
              couponStateChange={couponStateChange}
              couponData={couponData}
              setSelectCouponArray={setSelectCouponArray}
              setCheckDownload={setCheckDownload}
              checkDownload={checkDownload}
              setStateChange={setIsDownloadAllCoupon}
              setDownLoadCheck={setDownLoadCheck}
              memberCheckPoint={memberCheckPoint}
              festivalId={festivalData?.festival.id}
            />
          ))}
        </ul>
        <div
          className={
            hasAccessToken
              ? checkCount === downloadCheck.length
                ? 'all_download used'
                : 'all_download'
              : 'all_download'
          }
        >
          <BtnHomeModuleMore onClick={async () => await homeModuleMoreButtonHandler(el)}>
            {getHomeModuleMoreButtonText}
          </BtnHomeModuleMore>
        </div>
      </div>
    </>
  )
}

export default forwardRef(FestivalCouponModule)
